import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription, combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

import { ProjectsService, ProjectsEditorService } from '@app/services/app/projects.service';
import { AppService } from 'app/services/app/app.service';
import { VersionsViewService } from '@app/services/app/versions.service';
import { AppRouterService } from 'app/services/app/router.service';
import { AccessProject, AccessProjectKeys } from '@app/models/access';

import * as Objects from '@core/objects';
import { AccountSession } from '@core/objects/account-session';
import { TitleService } from '@core/services/title.service';
import { UrlService } from '@core/services/url/url.service';
import { NotificationsService } from '@core/services/notifications/notifications.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { FileSignatureManagerService } from '@core/services/files/file-signature-manager.service';

import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';

@Component({
    selector: 'project-version-file-preview-main',
    templateUrl: './version-file-preview.component.html',
    styleUrls: ['./version-file-preview.component.scss']
})
export class ProjectDetailsVersionDetailsFilePreviewComponent implements OnInit, OnDestroy {
    protected readonly PLUGIN_SEND_FILES = 'share_transfer';
    protected readonly APP_FILECOMPARISON = 'filecomparison';
    protected readonly ACCESS_GLOBAL_XRAY = 'access_global_xray';
    protected readonly PLUGIN_APOLLO = 'apollo'; // transitional: also gate "Compare" on this until apollo is fully retired

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_ERROR = 2;
    public readonly STATUS_FILE_NOT_FOUND = 4;
    public readonly STATUS_NOT_ALLOWED = 5;
    public readonly STATUS_NOT_FOUND= 6;

    public version: Objects.Version = null;
    public project: Objects.Request = null;
    public projectHeader: Objects.Request = null;
    public originProject: Objects.Request = null;
    public accessProject: AccessProject;
    public fileId: number | null = null;
    public file: Objects.AmsFile = null;
    public versionFiles: Objects.AmsFile[];
    public excludedLabels: Array<Objects.Label> = [];
    public versionStatusList: Array<Objects.Status> = null;
    public notLinkedArtwork: boolean = false;

    public context: string = '';

    public breadcrumbs: any = [
    ];
    public pluginsAccess: Array<string> = [];
    public appsAccess: Array<string> = [];
    public accessGlobalXray: boolean = true;
    public menu: Array<ThreeDotsMenuItem> = [{
        title: "Download File",
        key: "download",
        icon: "download",
        order: 1
    }];

    // optional menu
    protected menuSendFiles: ThreeDotsMenuItem = {
        title: "Send",
        key: "send-files",
        icon: "send-files",
        order: 2
    };
    protected menuCompare: ThreeDotsMenuItem = {
        title: "Compare",
        key: "compare",
        icon: "compare",
        order: 3
    };
    protected menuComparison: ThreeDotsMenuItem = {
        title: "NEW Filecomparison",
        htmltitle: "<strong>NEW</strong> Filecomparison",
        key: "comparison",
        icon: "compare",
        order: 4
    };
    // Menu pdf with comments its added in child where we can check notes or not

    public status:number = this.STATUS_LOADING;

    protected subs: Subscription = new Subscription();

    constructor(private title: TitleService,
        private app: AppService,
        private urlSrv: UrlService,
        private appRouter: AppRouterService,
        private activeRoute: ActivatedRoute,
        private projectsSrv: ProjectsService,
        private projectEditorSrv: ProjectsEditorService,
        private notifSrv: NotificationsService,
        private viewVersionsSrv: VersionsViewService,
        private fileDownloadSrv: FileDownloadService,
        private signatureManager: FileSignatureManagerService) { }

    ngOnInit() {
        this.setDefaultBredcrumb();
        this.title.set("Preview File... - Artwork... - Project...");
        this.subs.add(this.activeRoute.queryParams.subscribe((params: any) => {
            console.log("query params ===>", params);
            if (params.context) {
                this.context = params.context;
            }
        }));

        this.subs.add( this.projectsSrv.selectProject().subscribe((project: Objects.Request) => {
            this.project = project;
            this.initView();
        }));

        this.subs.add(this.projectsSrv.selectProjectHeader().subscribe((header: Objects.Request) => {
            if (header == null) return;
            this.projectHeader = header;
            this.initView()
        }));

        this.subs.add(this.projectEditorSrv.selectAccessAll().subscribe((access: AccessProject) => {
            if (access != null) {
                this.accessProject = access;
                this.initView();
            }
        }));

        this.subs.add(this.viewVersionsSrv.selectVersion().subscribe((version: Objects.Version) =>
        {
            this.version = version;
            this.initView();
        }));

        this.subs.add(this.viewVersionsSrv.selectProject().subscribe((projectOrigin: Objects.Request) => {
            this.originProject = projectOrigin;
            this.initView();
        }));

        this.subs.add(this.activeRoute.params.subscribe((params: any) => {
            // update march 2026  diego@twonas.com: clear files always before load new version to avoid keep
            // files from previous loaded version.
            //  This is required because files are not loaded together with version, 
            // so we can have old files while new version is loading due to quick leave on previous screen. 
            // This caused a not found displayed while leaving old version preview to open new one. This clean beofre load a new verison.
            this.viewVersionsSrv.clearAssets();
            this.viewVersionsSrv.load(+params.vid);
            this.fileId = +params.fid;
            this.file = null;
            this.status = this.STATUS_LOADING;
            this.initView();
        }));

        this.subs.add(this.viewVersionsSrv.selectAssets().subscribe((assets: Objects.AmsFile[]) => {
            this.versionFiles = assets;
            this.initView();
        }));

        this.subs.add(combineLatest([this.app.getSession(), this.app.getAccountSession()]).subscribe( ([session, accountSession]: [any, AccountSession]) => {
            this.pluginsAccess = session.options.plugins;
            // apps list now comes from the accounts-api session; fall back to the legacy session field
            // in case the (separate, parallel) accounts-api call hasn't resolved yet
            this.appsAccess = accountSession?.current?.apps ?? session.options.organization.apps;
            this.accessGlobalXray = session.options.access?.hasOwnProperty(this.ACCESS_GLOBAL_XRAY) ? session.options.access[this.ACCESS_GLOBAL_XRAY] : true;
            this.updateMenu(this.menuCompare,this.checkCompareAccess());
            this.updateMenu(this.menuComparison,this.checkFilecomparisonAccess());
            this.initView();
        }));

        this.subs.add(this.viewVersionsSrv.selectError('load_version').subscribe((error: HttpErrorResponse) => {
            this.setError(error);
        }));

    }

    ngOnDestroy() {
        this.subs.unsubscribe();
        // option instead clean version from store on project versions is to 
        // clear here version ASSETS after leave preview page to avoid assets from one version being loaded or checked after
        // but this not show precisely the version number on the breadcrums
        // this.viewVersionsSrv.clearAssets();
    }

    onMenuClick(menu: AmsFilesListItemMenuClick) {
        switch(menu.key) {
            case "download-comments": 
                // start notification to download the pdf with the annotations in it. We need the url for the prefix of the download and the file.
                this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FILE_ANNOTATIONS_START, {id: this.version.id, details: { url: "/versions/"+this.version.id+ "/feedbacks", amsfile: menu.file}});
                break;
            case "download":
                this.handleFileDownload(menu.file);
                break;
            case "send-files":
                if (this.checkPluginAccess(this.PLUGIN_SEND_FILES)){
                    if(this.originProject != null)
                        this.appRouter.navigate('/p/transfer/'+this.originProject.id, { queryParams: { file: +menu.file.id, version: this.version.id } });
                }
                break;
            case "compare":
                this.appRouter.navigate('/p/apollo/request/'+this.originProject.id);
                break;
            case "comparison":
                this.appRouter.navigate('/filecomparison/project/'+this.originProject.id+'/artwork/'+this.version.id+'/file/'+menu.file.uuid);
                break;
        }
    }

    onActionDownloadClick(file: Objects.AmsFile){
        this.handleFileDownload(file);
    }

    private initView() {
        if ((this.project != null || this.projectHeader != null) && this.version != null)
            this.updateBreadcrumbs();

        if ((this.project != null && this.projectHeader != null) || this.version == null || this.originProject == null || this.versionFiles == null || this.fileId == null) {
            return;
        }

        if (this.versionFiles.length > 0 && this.fileId != null) {
            // find the file in the version files using uuid if available, otherwise id
            this.file = this.versionFiles.find((f: Objects.AmsFile) => this.filesMatch(f, +this.fileId));
            if (!this.file) {
                this.status = this.STATUS_FILE_NOT_FOUND;
                return;
            }
        } else {
            this.status = this.STATUS_FILE_NOT_FOUND;
            return;
        }

        this.setHeaderTitle();
        this.setVersionMenu();
        this.status = this.STATUS_DONE;
    }

    protected checkPluginAccess(plugin: string){
        return this.pluginsAccess.some((pa) => pa == plugin);
    }

    protected checkAppAccess(app: string){
        return this.appsAccess.some((a) => a == app);
    }

    protected checkCompareAccess(): boolean {
        return this.accessGlobalXray && this.checkPluginAccess(this.PLUGIN_APOLLO);
    }

    protected checkFilecomparisonAccess(): boolean {
        return this.accessGlobalXray && this.checkAppAccess(this.APP_FILECOMPARISON);
    }

    // this function check either remove or add item. based on subscribed events
    protected updateMenu(item: ThreeDotsMenuItem, addItem:boolean = true) {
        this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
        if(addItem){
            this.menu.push(item);
        }
    }

    protected setVersionMenu() : void {
        this.updateMenu(this.menuSendFiles, this.checkPluginAccess(this.PLUGIN_SEND_FILES) && this.accessProject.hasOwnProperty(AccessProjectKeys.SEND_FILE) && this.accessProject[AccessProjectKeys.SEND_FILE]);
    }

    protected updateBreadcrumbs(){ // update intermediate breadcrumb depending on context
        if ((this.project == null && this.projectHeader == null) || this.version == null) {
            return;
        }
        switch (this.context) {
            case "origin": // by now, origin will be on its original project url and version
                // this.excludeLabels = this.originProject.labels;

                this.excludedLabels = this.version.labels.filter((vLabel: Objects.Label) =>
                    this.originProject.labels.some((pLabel: Objects.Label) => pLabel.id === vLabel.id)
                );
                this.breadcrumbs[0] = {link: '/projects/'+this.originProject.id,
                    title: 'Project <small>(#'+this.originProject.id+ ')</small>'};
                this.breadcrumbs[1] = {link: "/versions/"+this.version.id,
                    title: 'Artwork v.'+this.version.version+' <small>(#'+this.version.id+')</small>'};
                break;
            default:
                const currentProject = this.project != null ? this.project : this.projectHeader;
                // this.excludeLabels = this.project.labels;
                this.excludedLabels = this.version.labels.filter((vLabel: Objects.Label) =>
                    currentProject.labels.some((pLabel: Objects.Label) => pLabel.id === vLabel.id)
                );
                this.breadcrumbs[0] = {link: '/projects/'+currentProject.id,
                                        title: 'Project <small>(#'+currentProject.id+ ')</small>'};
                this.breadcrumbs[1] = {link: '/projects/'+currentProject.id+"/versions/"+this.version.id,
                    title: 'Artwork v.'+this.version.version+' <small>(#'+this.version.id+')</small>'};
                break;
        }
    }

    private setHeaderTitle() {
        this.title.set("Preview File "+this.file.name+ " - Artwork #"+ this.version.id + "- Project #" + (this.project ? this.project.id : this.projectHeader.id));
    }

    protected setDefaultBredcrumb(){
        this.breadcrumbs[0] = {link: '',
                    title: 'Project <small>(# .  .  . )</small>'};
        this.breadcrumbs[1] = {link: "",
                    title: 'ARTWORK v. . . . <small>(# .  .  . )</small>'};

    }
    protected setError(error: HttpErrorResponse) {
        // console.log("we have file or version errors ",error)
        if(error == null)
            return;
        switch(error.status) {
            case 500:
                this.status = this.STATUS_ERROR;
                break;
            case 404:
                this.status = this.STATUS_NOT_FOUND;
                break;
            case 403:
                this.viewVersionsSrv.clearAssets();
                this.file = null;
                this.status = this.STATUS_NOT_ALLOWED;
                break;
        }
    }

    /**
     * Compare files by UUID if present, otherwise by ID
     * Future-proofed for UUID-based file identification
     */
    private filesMatch(file: Objects.AmsFile, compareWith: Objects.AmsFile | number): boolean {
        if (!file) return false;
        
        // If comparing with another file object, use UUID if both have it
        if (typeof compareWith === 'object') {
            if (file.uuid && compareWith.uuid) {
                return file.uuid === compareWith.uuid;
            }
            return file.id === compareWith.id;
        }
        
        // If comparing with a number (ID), use ID comparison
        return file.id === compareWith;
    }

    /**
     * Handle file download with signature expiration check
     * If signature is expired, refresh assets and retry download
     */
    private handleFileDownload(file: Objects.AmsFile) {
        if (file && file.access && this.signatureManager.isSignatureExpired(file)) {
            // Signature expired - refresh assets and wait for fresh signatures
            this.viewVersionsSrv.refreshAssetsSignatures();
            // Wait for store to have a file with fresh signature before downloading
            // Use predicate to ensure we get a file with valid signature
            this.viewVersionsSrv.selectAssets().pipe(
                first((assets: Objects.AmsFile[]) => {
                    // Ensure we have the file and its signature is fresh
                    const updatedFile = assets?.find((f: Objects.AmsFile) => this.filesMatch(f, file));
                    return updatedFile && !this.signatureManager.isSignatureExpired(updatedFile);
                })
            ).subscribe((assets: Objects.AmsFile[]) => {
                const updatedFile = assets.find((f: Objects.AmsFile) => this.filesMatch(f, file));
                if (updatedFile) {
                    this.fileDownloadSrv.downloadVersionAsset(updatedFile);
                } else {
                    // Fallback if file not found after refresh
                    window.location.href = this.urlSrv.build('/files/'+file.id);
                }
            });
        } else if (file && file.access) {
            // Signature is valid, use the download service
            this.fileDownloadSrv.downloadVersionAsset(file);
        } else {
            // No signature, fall back to legacy download
            window.location.href = this.urlSrv.build('/files/'+file.id);
        }
    }

}
